<script setup>
/**
 * LiveConference.vue
 * ---------------------------------------------------------------------------
 * Single-file live audio room component.
 *
 * Assumes:
 *  - axios is available via inject("axios")
 *  - Vuex: store.state.user.uid (current user), store.state.baseUrl (API/image
 *    origin), store.state.users (userId -> {pseudo, profilImgMini1, ...}
 *    cache), and a `setAnUser` mutation, exactly as wired in the version you
 *    sent back.
 *  - backend routes: POST /conference.php (multipart/form-data, field "type")
 *  - npm install livekit-client
 *  - a WebSocket board endpoint at /board (see boardServer.ts)
 *
 * Admin = conference.userId === store.state.user.uid. Admin can do everything
 * the backend exposes except setVolume.
 *
 * Call `show(conferenceDoc)` (via a template ref) to open the live.
 * ---------------------------------------------------------------------------
 */
/* eslint-disable no-undef */
import { ref, reactive, computed, onBeforeUnmount, inject } from "vue";
import { useStore } from "vuex";
import { Room, RoomEvent, Track } from "livekit-client";
import LiveBoard from "./LiveBoard.vue";
import imageCompression from 'browser-image-compression'
import ParticipantPicker from './ParticipantPicker.vue'
import AlertW from '@/views/components/alertW/AlertW.vue'
import { ForegroundService } from '@capawesome-team/capacitor-android-foreground-service';
import { Capacitor } from '@capacitor/core';

import { nativeConnect, nativeSetMic/*, nativeDisconnect*/, onNative,/* nativeSetRemoteVolume,*/ nativeSetEchoCancellation } from '@/plugin/liveKitNative';

const useNativeAudio = Capacitor.getPlatform() === 'android';

const alertW = ref(null)

const emit = defineEmits(["ended", "left", "showProfile"]);

const axios = inject("axios");
const store = useStore();

const currentUserId = computed(() => store.state.user.uid);
const bsrImg = store.state.baseUrl + "images/";
const selectedParticipants = ref([])
const pickerOpen = ref(false)

const conference = ref(null)
const isAdmin = computed(
  () => !!conference.value && String(conference.value.userId) === String(currentUserId.value)
);

// All actor slots (invited speakers), whether currently connected or not.
const actors = reactive([]);
// Entitlement to a speaker token = "is in the actors list at all", not "is
// currently connected" — checking state !== "outside" would make it
// impossible for anyone but the admin to ever get their first speaker token.
const isActor = computed(() =>
  actors.some((a) => String(a.userId) === String(currentUserId.value))
);

const MAX_SPEAKERS = 10;
const openSpeakerSlots = computed(() => MAX_SPEAKERS - actors.length);

// --- connection / UI state ---
const Display = ref(false);
const connecting = ref(true);
const connected = ref(false);
const ending = ref(false);
const error = ref("");
const micEnabled = ref(true);
const numParticipants = ref(0);
const showInfo = ref(false);
const showBanned = ref(false);
const showBoard = ref(false);
const commentsExpanded = ref(false);
const openActionsFor = ref(null); // identity, or null
const usingNative = ref(false);

// description editing
const editingText = ref(false);
const editText = ref("");

// echo
const echoCancellation = ref(true); // on by default, per spec
const showEchoInfo = ref(false);

let deviceChangeDebounce = null;

// identity -> { speaking, micMuted, canPublish }
const liveState = reactive({});
function liveFor(identity) {
  if (!liveState[identity]) liveState[identity] = { speaking: false, micMuted: false, canPublish: true };
  return liveState[identity];
}

// identity -> boolean, true once we've actually seen them connected to the LiveKit room
const onlineState = reactive({});
function isOnline(identity) {
  return !!onlineState[identity];
}

let room = null;
const audioEls = new Map();

// --- comments ---
const comments = reactive([]);
const newComment = ref("");
let syncWs = null;
let syncReconnectTimer = null;

// --- subscriber-side (received audio) leveling ---
// track.sid -> { ctx, source }. Compresses each incoming stream on the
// LISTENING side instead of processing your own outgoing mic — this is what
// lets capture go back to plain setMicrophoneEnabled() below, and also
// naturally balances multiple speakers' differing mic loudness for whoever
// is listening, independent of any one speaker's own settings.
const audioNodeMap = new Map();

//const processedAudioEls = new WeakSet(); // add near audioNodeMap
/* 
function normalizeSubscriberAudio(audioElement, participantId) {
  if (processedAudioEls.has(audioElement)) {
    // Already wired this exact element once. WebAudio forbids calling
    // createMediaElementSource on the same element twice, ever — if we've
    // already claimed it, just make sure its context isn't suspended.
    const existing = audioNodeMap.get(participantId);
    if (existing && existing.ctx.state === "suspended") existing.ctx.resume().catch(() => {});
    return;
  }

  if (audioNodeMap.has(participantId)) {
    const existing = audioNodeMap.get(participantId);
    if (existing.ctx.state === "suspended") existing.ctx.resume().catch(() => {});
    return;
  }

  const AudioCtxClass = window.AudioContext || window.webkitAudioContext;
  const audioCtx = new AudioCtxClass();

  audioCtx.onstatechange = () => {
    if (audioCtx.state === "suspended") audioCtx.resume().catch(() => {});
  };

  try {
    const source = audioCtx.createMediaElementSource(audioElement);
    processedAudioEls.add(audioElement); // mark it claimed, right after the call that can only succeed once
    const compressor = audioCtx.createDynamicsCompressor();
    compressor.threshold.setValueAtTime(-16, audioCtx.currentTime);
    compressor.knee.setValueAtTime(24, audioCtx.currentTime);
    compressor.ratio.setValueAtTime(4, audioCtx.currentTime);
    compressor.attack.setValueAtTime(0.01, audioCtx.currentTime);
    compressor.release.setValueAtTime(0.1, audioCtx.currentTime);


    const boost = audioCtx.createGain();
    boost.gain.value = 2.5; // tune to taste — this is the "louder" knob
    source.connect(compressor);
    compressor.connect(boost);
    boost.connect(audioCtx.destination);

    audioCtx.resume().catch(() => {});
    audioNodeMap.set(participantId, { ctx: audioCtx, source });
  } catch (err) {
    console.warn("AudioContext routing fallback:", err);
  }
}
 */
async function handleDeviceChange() {
  // Output side: resume any subscriber AudioContext a device switch
  // suspended (a common side-effect on mobile when the OS reroutes audio).
    if (usingNative.value) return; // native SDK's own audio engine handles device switching — nothing to do from JS here

  for (const [, node] of audioNodeMap) {
    if (node.ctx.state === "suspended") {
      try { await node.ctx.resume(); } catch {console.log('done')}
    }
  }

  // Input side: the fix above only fixes what you HEAR. Your own mic
  // capture stays bound to whichever device was default when it was
  // opened, so re-acquire it against the new default too — debounced,
  // since a single physical plug/unplug can fire this event more than once.
  clearTimeout(deviceChangeDebounce);
  deviceChangeDebounce = setTimeout(async () => {
    if (!isActor.value || !micEnabled.value || !rooandroidm) return;
    try {
      const pub = room.localParticipant.getTrackPublication(Track.Source.Microphone);
      if (pub?.audioTrack) await pub.audioTrack.restartTrack(micConstraints());
    } catch (e) {
      error.value = apiError(e, "Could not switch to the new audio device.");
    }
  }, 800);
}


// ------- capacitor functions -----------------------------
async function startMicForegroundService() {
  if (Capacitor.getPlatform() !== 'android') return; // no-op on web/iOS
  try {
    const perms = await ForegroundService.checkPermissions();
    if (perms.notifications !== 'granted') {
      await ForegroundService.requestPermissions();
    }
    await ForegroundService.startForegroundService({
      id: 1,
      title: 'Live in progress',
      body: 'Your microphone stays active while this live continues',
      smallIcon: 'ic_stat_icon_config_sample', // replace with a real icon resource in your app
      serviceType: 'microphone',
    });
  } catch (e) {
    console.warn('Could not start foreground service:', e);
  }
}

async function stopMicForegroundService() {
  if (Capacitor.getPlatform() !== 'android') return;
  try {
    await ForegroundService.stopForegroundService();
  } catch (e) {
    console.warn('Could not stop foreground service:', e);
  }
}
//------ echo cancellation management ---
function micConstraints() {
  return {
    echoCancellation: echoCancellation.value,
    noiseSuppression: false,
    autoGainControl: true
  };
}

async function onEchoCancellationChange(value) {
  echoCancellation.value = value;
  if (!isActor.value || !micEnabled.value) return;

  if (usingNative.value) {
    try {
      await nativeSetEchoCancellation(value);
    } catch (e) {
      error.value = apiError(e, "Could not update mic settings.");
    }
    return;
  }

  if (!room) return;
  try {
    const pub = room.localParticipant.getTrackPublication(Track.Source.Microphone);
    if (pub?.audioTrack) await pub.audioTrack.restartTrack(micConstraints());
  } catch (e) {
    error.value = apiError(e, "Could not update mic settings.");
  }
}

let iosUnlockCtx = null;
const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

async function unlockIOSAudio() {
  if (!isIOS) return;
  try {
    const AudioCtxClass = window.AudioContext || window.webkitAudioContext;
    iosUnlockCtx = new AudioCtxClass();
    if (iosUnlockCtx.state === "suspended") await iosUnlockCtx.resume();
    const buffer = iosUnlockCtx.createBuffer(1, 1, 22050);
    const source = iosUnlockCtx.createBufferSource();
    source.buffer = buffer;
    source.connect(iosUnlockCtx.destination);
    source.start(0);
  } catch (e) {
    console.warn("iOS audio unlock failed:", e);
  }
}
//--- comments pop ---------
const selectedComment = ref(null); // userId, or null when the menu is closed

function openUserMenu(user) {
  selectedComment.value = user;
}
function closeUserMenu() {
  selectedComment.value = null;
}
function isUserBanned(userId) {
  return (conference.value.bannedUsers || []).some((b) => String(b) === String(userId));
}
function viewProfile(user) {
  emit('showProfile',user)
  closeUserMenu();
}
async function toggleBanFromChat(userId) {
  if (isUserBanned(userId)) {
    await unbanUser(userId);
  } else {
    await banActor(userId); // already generic — bans by userId regardless of role
  }
  closeUserMenu();
}

//------------ sharing ----------
async function onShare() {
  const url = store.state.baseUrl + 'conference/' + conference.value._id
  if (navigator.share) {
    try { await navigator.share({ title: 'Copy conference link' || '', url }); return }
    catch (e) { if (e.name === 'AbortError') return }
  }
  try {
    await navigator.clipboard.writeText(url)
    error.value = apiError({response:{}}, "link copied");
  } catch (e) { console.error(e) }
}
//------------
function form(type, fields = {}) {
  const f = new FormData();
  f.append("type", type);
  f.append("conferenceId", conference.value._id);
  Object.entries(fields).forEach(([k, v]) => f.append(k, v));
  return f;
}

function apiError(e, fallback) {
  setTimeout(() => { error.value = "" }, 5000) // make the error message disapear
  return e.response?.data?.error || e.message || fallback;
}

function getUser(userId) {
  //alert(userId)
  //alert(JSON.stringify(store.state.users[userId]))
  return store.state.users[userId] || {};
}

// ---------------------------------------------------------------------------
// Open / close
// ---------------------------------------------------------------------------

function hide() {
  Display.value = false;
}
function unhide() {
  Display.value = true;
  if(!connected.value) show(conference.value)
}

async function show(conf) {
  unlockIOSAudio(); // must be the first thing here — synchronous call, still inside the click that triggered show()
  conference.value = conf;
  if (!conference.value.stats) {
    conference.value.stats = { shares: 0, views: 0, likes: 0, comments: 0 };
  }
  actors.splice(0, actors.length, ...(conf.actors || []).map((a) => ({ ...a })));
  numParticipants.value = conf.numberOfParticipant || 0;
  showInfo.value = false;
  showBoard.value = false;
  editingText.value = false;
  comments.splice(0, comments.length); // voiding comments array
  liked.value = false
  echoCancellation.value = true
  Display.value = true;

  await connect();
  await refreshConferenceState()
  await fetchComments();
  getActorsData();

  connectSync();
}

defineExpose({ show, unhide, connected, room, hide, Display });

// ---------------------------------------------------------------------------
// Periodic state refresh (actors list / bans / ended-elsewhere sync)
// ---------------------------------------------------------------------------

async function refreshConferenceState() {
  if (!conference.value) return;
  try {
    const { data } = await axios.post("/conference.php", form("getConference"));
    if (!data || !data._id) return;

    conference.value = { ...conference.value, ...data };
    if (!conference.value.stats) {
      conference.value.stats = { shares: 0, views: 0, likes: 0, comments: 0 };
    }
    actors.splice(0, actors.length, ...(data.actors || []).map((a) => ({ ...a })));

    if (data.end && !isAdmin.value) {
      await teardown();
      Display.value = false;
      emit("left");
    }
  } catch {
    // non-fatal — the next "state" push will trigger another attempt
  }
}

/**
 * Pushed comment/state updates instead of polling. Separate from the
 * board's own WebSocket (/board) — this one stays open for the whole time
 * the live is being viewed, not just while a board happens to be open.
 */
function connectSync() {

  const proto = location.protocol === "https:" ? "wss:" : "ws:";
  const host = new URL(store.state.baseUrl).host;
  const token = localStorage.getItem("access_token");
  syncWs = new WebSocket(`${proto}//${host}/conference-sync?conferenceId=${conference.value._id}&token=${encodeURIComponent(token)}`);


  syncWs.onmessage = async (evt) => {
    let msg;
    try { msg = JSON.parse(evt.data); } catch { return; }

    if (msg.t === "comment") {
      // Dedupe against the optimistic push already done in postComment()
      // for the sender's own comment.

      // getting sender info
      if (!store.state.users[msg.comment.userId]) {
        const res2 = await axios.post("user.php", { type: "getUsersMetaData", users: [msg.comment.userId] });
        const users = res2.data || [];
        // alert(JSON.stringify(users[0]))
        if(users[0]) store.commit("setAnUser", users[0])
      }

      if (!comments.some((c) => c._id === msg.comment._id)) comments.push(msg.comment);
    } 
    else if (msg.t === "commentDeleted") {
      const i = comments.findIndex((c) => c._id === msg.comId);
      if (i !== -1) comments.splice(i, 1);
    } 
    else if (msg.t === "likes") {
      if (conference.value.stats) conference.value.stats.likes = msg.likes;
    }
    else if (msg.t === "conference") {
      const data = msg.conference;
      conference.value = { ...conference.value, ...data };
      if (!conference.value.stats) {
        conference.value.stats = { shares: 0, views: 0, likes: 0, comments: 0 };
      }
      actors.splice(0, actors.length, ...(data.actors || []).map((a) => ({ ...a })));
      // deliberately NOT touching numParticipants here — still driven by the
      // live LiveKit roster, per the earlier fix
      if (data.end && !isAdmin.value) {
        teardown();
        Display.value = false;
        emit("left");
      }
    }
  };

  syncWs.onclose = () => {
    syncWs = null;
    if (Display.value) syncReconnectTimer = setTimeout(connectSync, 2000);
  };
}

function disconnectSync() {
  clearTimeout(syncReconnectTimer);
  if (syncWs) {
    syncWs.onclose = null; // don't auto-reconnect on a deliberate close
    syncWs.close();
    syncWs = null;
  }
}

// ---------------------------------------------------------------------------
// LiveKit connection
// ---------------------------------------------------------------------------

async function connect() {
  connecting.value = true;
  error.value = "";
  try {
    const type = isActor.value ? "joinConference" : "listenConference";
    const { data } = await axios.post("/conference.php", form(type));

    // Try the native Android audio path first (if applicable), but don't
    // let a missing/not-yet-registered native plugin take down the whole
    // join — fall back to the standard WebView LiveKit client instead.
    // Remove this fallback once LiveKitPlugin is confirmed registered in
    // MainActivity and you want native to be a hard requirement on Android.
    if (useNativeAudio) {
      try {
        onNative('participantConnected', ({ identity }) => {
          if (!onlineState[identity]) {
            onlineState[identity] = true;
            numParticipants.value++;
          }
          //nativeSetRemoteVolume(identity, 1.6).catch(() => {}); // tune this multiplier to taste
        });
        onNative('participantDisconnected', ({ identity }) => {
          if (onlineState[identity]) {
            onlineState[identity] = false;
            numParticipants.value = Math.max(0, numParticipants.value - 1);
          }
        });
        onNative('activeSpeakersChanged', ({ identities }) => {
          const ids = new Set(identities);
          actors.forEach((a) => (liveFor(a.userId).speaking = ids.has(String(a.userId))));
        });
        onNative('trackMuted', ({ identity }) => { liveFor(identity).micMuted = true; });
        onNative('trackUnmuted', ({ identity }) => { liveFor(identity).micMuted = false; });

        onlineState[currentUserId.value] = true;
        numParticipants.value = 1; // count self; native events add the rest

        await nativeConnect(store.state.livekitUrl, data.token);

        if (data.role === 'speaker') {
          await nativeSetMic(true);
          await nativeSetEchoCancellation(echoCancellation.value); // actually apply the real setting, not just the SDK default
        }
        usingNative.value = true; // see Bug 3 below — new ref, add it near your other refs
      } catch (e) {
        console.warn("Native audio path unavailable, falling back to WebView LiveKit client:", e);
      }
    }

    if (!usingNative.value) {
      room = new Room({
        adaptiveStream: true,
        dynacast: true,
        publishDefaults: {
          audioPreset: { maxBitrate: 48000 }, // 48kbps ceiling
          dtx: false, // discontinuous transmission — drops bitrate further during silence, on by default but worth being explicit
        },
      });

      registerRoomEvents();

      await room.connect(store.state.livekitUrl, data.token);
      navigator.mediaDevices.addEventListener("devicechange", handleDeviceChange);

      onlineState[room.localParticipant.identity] = true;
      room.remoteParticipants.forEach((p) => (onlineState[p.identity] = true));

      if (data.role === "speaker") {useNativeAudio
        await room.localParticipant.setMicrophoneEnabled(true, micConstraints());
        micEnabled.value = true;
        await startMicForegroundService();
        await room.localParticipant.setMicrophoneEnabled(false);
        await room.localParticipant.setMicrophoneEnabled(true, micConstraints());
        onEchoCancellationChange(echoCancellation.value)
      }
      numParticipants.value = room.remoteParticipants.size + 1;
    }
    connected.value = true;
  } catch (e) {
    error.value = apiError(e, "Could not join the live.");
  } finally {
    connecting.value = false;
  }
}

function registerRoomEvents() {
  room.on(RoomEvent.ParticipantConnected, (p) => {
    onlineState[p.identity] = true;
    numParticipants.value = room.remoteParticipants.size + 1;
  });
  room.on(RoomEvent.ParticipantDisconnected, (p) => {
    onlineState[p.identity] = false;
    numParticipants.value = room.remoteParticipants.size + 1;
  });

  room.on(RoomEvent.ActiveSpeakersChanged, (speakers) => {
    const ids = new Set(speakers.map((s) => s.identity));
    actors.forEach((a) => (liveFor(a.userId).speaking = ids.has(String(a.userId))));
  });

  room.on(RoomEvent.TrackMuted, (pub, participant) => {
    if (pub.kind === Track.Kind.Audio) liveFor(participant.identity).micMuted = true;
  });
  room.on(RoomEvent.TrackUnmuted, (pub, participant) => {
    if (pub.kind === Track.Kind.Audio) liveFor(participant.identity).micMuted = false;
  });


  // after
  room.on(RoomEvent.ParticipantPermissionsChanged, async (prevPermissions, participant) => {
    liveFor(participant.identity).canPublish = participant.permissions?.canPublish ?? false;
    // This is us, just promoted from listener to speaker while already
    // connected. The LiveKit permission flips live via this event, but
    // nothing automatically opens the mic and starts publishing — that's
    // the actual gap causing "the mic isn't set up."
    if (
      participant.identity === room.localParticipant.identity &&
      participant.permissions?.canPublish &&
      !room.localParticipant.getTrackPublication(Track.Source.Microphone)
    ) {
      try {
      
        await room.localParticipant.setMicrophoneEnabled(true, micConstraints());
        micEnabled.value = true;
        await startMicForegroundService();
        // Same renegotiation workaround as the initial-join case — the very
        // first publish after a permission grant can end up not actually
        // audible until a mute/unmute cycle forces one.
        await room.localParticipant.setMicrophoneEnabled(false);
        await room.localParticipant.setMicrophoneEnabled(true, micConstraints());
        onEchoCancellationChange(true)
      } catch (e) {
        error.value = apiError(e, "Could not enable your microphone.");
      }
    }
  });
/* 
  room.on(RoomEvent.TrackSubscribed, (track) => {
    if (track.kind !== Track.Kind.Audio) return;
    const el = track.attach();
    el.autoplay = true;
    audioEls.set(track.sid, el);
    document.body.appendChild(el);
    normalizeSubscriberAudio(el, track.sid);
  });
  room.on(RoomEvent.TrackUnsubscribed, (track) => {
    const el = audioEls.get(track.sid);
    if (el) {
      track.detach(el);
      el.remove();
      audioEls.delete(track.sid);
    }
    const node = audioNodeMap.get(track.sid);
    if (node) {
      node.ctx.close().catch(() => {});
      audioNodeMap.delete(track.sid);
    }
  }); */

  // after
  room.on(RoomEvent.TrackSubscribed, (track) => {
    if (track.kind !== Track.Kind.Audio) return;
    // Building the element ourselves, instead of via track.attach(),
    // guarantees a brand-new DOM node every time — attach() can hand back a
    // previously-used element internally, which is what was causing the
    // "already connected to a different MediaElementSourceNode" crash.
    const el = document.createElement("audio");
    el.volume = 1;
    el.autoplay = true;
    if(isIOS) { 
      el.setAttribute("playsinline", "");
      el.setAttribute("webkit-playsinline", "");
    }

    
    el.srcObject = new MediaStream([track.mediaStreamTrack]);
    audioEls.set(track.sid, el);
    document.body.appendChild(el);

/*     if (!isActor.value) {
      normalizeSubscriberAudio(el, track.sid);
    } */
  });
  room.on(RoomEvent.TrackUnsubscribed, (track) => {
    const el = audioEls.get(track.sid);
    if (el) {
      el.srcObject = null;
      el.remove();
      audioEls.delete(track.sid);
    }
    const node = audioNodeMap.get(track.sid);
    if (node) {
      node.ctx.close().catch(() => {});
      audioNodeMap.delete(track.sid);
    }
  });

    room.on(RoomEvent.Disconnected, () => {
      connected.value = false;
    });
  }

/**
 * Disconnect the LiveKit client deliberately, BEFORE telling the backend to
 * end/leave — see earlier notes: this avoids LiveKit's client auto-reconnect
 * kicking in on what would otherwise look like an unexpected disconnect.
 */
/* async function teardown() {
  disconnectSync();
  if (room) {
    await room.disconnect();
    room = null;
  }
  audioEls.forEach((el) => el.remove());
  audioEls.clear();
  audioNodeMap.forEach((node) => node.ctx.close().catch(() => {}));
  audioNodeMap.clear();
  navigator.mediaDevices.removeEventListener("devicechange", handleDeviceChange);
  connected.value = false;
} */

async function teardown() {
  disconnectSync();
  await stopMicForegroundService();
  if (room) {
    await room.disconnect();
    room = null;
  }
  audioEls.forEach((el) => el.remove());
  audioEls.clear();
  audioNodeMap.forEach((node) => node.ctx.close().catch(() => {}));
  audioNodeMap.clear();
  navigator.mediaDevices.removeEventListener("devicechange", handleDeviceChange);

  if (iosUnlockCtx) {
  try { 
    await iosUnlockCtx.close(); } catch {console.log('done')}
    iosUnlockCtx = null;
  }
  connected.value = false;
}

// ---------------------------------------------------------------------------
// Mic controls
// ---------------------------------------------------------------------------

async function toggleSelfMute() {
  if (!isActor.value) return;

  if (usingNative.value) {
    if (micEnabled.value) {
      micEnabled.value = false; // instant local mute, same as the web path
      try {
        await nativeSetMic(false);
      } catch (e) {
        micEnabled.value = true;
        error.value = apiError(e, "Could not mute your mic.");
        return;
      }
      axios.post("/conference.php", form("selfMute")).catch((e) => {
        error.value = apiError(e, "Could not sync mute state.");
      });
    } else {
      try {
        await axios.post("/conference.php", form("selfUnmute")); // backend-first, same reasoning as web: admin lock can reject it
        await nativeSetMic(true);
        micEnabled.value = true;
      } catch (e) {
        error.value = apiError(e, "Could not update your mic.");
      }
    }
    return;
  }

  // --- existing WebView logic, completely unchanged below this line ---
  if (!room) return;
  const pub = room.localParticipant.getTrackPublication(Track.Source.Microphone);
  if (!pub) return;
  if (micEnabled.value) {
    micEnabled.value = false;
    try {
      await pub.mute();
    } catch (e) {
      micEnabled.value = true;
      error.value = apiError(e, "Could not mute your mic.");
      return;
    }
    try {
      await axios.post("/conference.php", form("selfMute"));
    } catch (e) {
      error.value = apiError(e, "Could not sync mute state.");
    }
  } else {
    try {
      await axios.post("/conference.php", form("selfUnmute"));
      await pub.unmute();
      micEnabled.value = true;
    } catch (e) {
      error.value = apiError(e, "Could not update your mic.");
    }
  }
}

// ---------------------------------------------------------------------------
// Admin actions
// ---------------------------------------------------------------------------

async function inviteActor(neoActors) {
  if (!neoActors || neoActors.length === 0) return;
  neoActors.forEach(async (act)=>{
    let conf = conference.value.actors.findIndex((ac)=> ac.userId === act.uid)
    if(conf !== -1)  return // actor already there
    try {
      await axios.post("/conference.php", form("addActor", { newActor: act.uid }));
      // actors.push({ userId: act.uid, volume: 1, state: "active" }); // should i delete this
    } catch (e) {
      error.value = apiError(e, "Could not add speaker.");
    }
  })
  closeUserMenu();
}

async function removeActor(identity) {
  try {
    await axios.post("/conference.php", form("removeActor", { actor: identity }));
    const i = actors.findIndex((a) => String(a.userId) === String(identity));
    if (i !== -1) actors.splice(i, 1);
  } catch (e) {
    error.value = apiError(e, "Could not remove speaker.");
  } finally {
    openActionsFor.value = null;
  }
}

async function toggleMuteActor(actor) {
  const identity = actor.userId;
  try {
    if (actor.state === "mute") {
      await axios.post("/conference.php", form("unmuteActor", { actor: identity }));
      actor.state = "active";
    } else {
      await axios.post("/conference.php", form("muteActor", { actor: identity }));
      actor.state = "mute";
    }
  } catch (e) {
    error.value = apiError(e, "Could not update speaker.");
  } finally {
    openActionsFor.value = null;
  }
}

async function banActor(identity) {
  try {
    await axios.post("/conference.php", form("banUser", { banned: identity }));
    const i = actors.findIndex((a) => String(a.userId) === String(identity));
    if (i !== -1) actors.splice(i, 1);
    if (!conference.value.bannedUsers) conference.value.bannedUsers = [];
    // conference.value.bannedUsers.push(identity);
  } catch (e) {
    error.value = apiError(e, "Could not ban user.");
  } finally {
    openActionsFor.value = null;
  }
}

async function unbanUser(identity) {
  try {
    await axios.post("/conference.php", form("unbanUser", { banned: identity }));
    conference.value.bannedUsers = (conference.value.bannedUsers || []).filter(
      (b) => String(b) !== String(identity)
    );
  } catch (e) {
    error.value = apiError(e, "Could not unban user.");
  }
}

// --- description editing (admin only) ---
function startEditText() {
  editText.value = conference.value.text;
  editingText.value = true;
}
async function saveText() {
  const t = editText.value.trim();
  if (!t) return;
  try {
    await axios.post("/conference.php", form("updateConference", { text: t }));
    conference.value.text = t;
    editingText.value = false;
  } catch (e) {
    error.value = apiError(e, "Could not update description.");
  }
}

async function onChangeCover(file) {
  try {
    // Compress the cover image before sending it off.
    let compressedCoverFile = null
    if(!(file.type === "image/svg+xml")) { // if it is not an svg
      const compressedCover = await imageCompression(file, {
        maxSizeMB: 1,
        maxWidthOrHeight: 800,
        useWebWorker: true,
        fileType: 'image/jpeg',
        initialQuality: 0.82
      })
      compressedCoverFile = new File(
        [compressedCover],
        file.name.replace(/\.[^.]+$/, '') + '.jpg',
        { type: 'image/jpeg' }
      )
    }
    const f = new FormData();
    f.append("type", "updateImage");
    f.append("conferenceId", conference.value._id);
    f.append("image", compressedCoverFile || file);

    const { data } = await axios.post("/conference.php", f);
    conference.value.img = data.img;
    conference.value.imgMini = data.imgMini;
  } catch (e) {
    alert(e)
    error.value = apiError(e, "Could not update cover image.");
  }
}

// --- like ---
const liked = ref(false);
async function toggleLike() {
  const wasLiked = liked.value;
  liked.value = !wasLiked;
  if (conference.value.stats) {
    conference.value.stats.likes = Math.max(0, (conference.value.stats.likes || 0) + (wasLiked ? -1 : 1));
  }
  try {
    await axios.post("/conference.php", form(wasLiked ? "unlike" : "like"));
  } catch (e) {
    liked.value = wasLiked;
    if (conference.value.stats) {
      conference.value.stats.likes = Math.max(0, (conference.value.stats.likes || 0) + (wasLiked ? 1 : -1));
    }
    error.value = apiError(e, "Could not update like.");
  }
}


// ---------------------------------------------------------------------------
// Comments
// ---------------------------------------------------------------------------

async function getActorsData() {
  try {
    const usersToGet = new Set();
    conference.value.actors.forEach((c) => {
      if (!store.state.users[c.userId]) usersToGet.add(c.userId);
    });
    if(!store.state.users[currentUserId.value]?.pseudo) usersToGet.add(currentUserId.value)
    if (usersToGet.size > 0) {
      const res2 = await axios.post("user.php", { type: "getUsersMetaData", users: [...usersToGet] });
      const users = res2.data || [];
      users.forEach((u) => store.commit("setAnUser", u));
    }
  } catch {
    // non-fatal
  }
}

async function fetchComments() {
  try {
    const res = await axios.post("/conference.php", form("commentList", { from: new Date().toISOString() }));
    const data = res.data || [];
    // commentList reads backwards from `from`, so it comes back newest-first —
    // flip it so the list renders oldest-at-top, newest-at-bottom (chat order),
    // matching how live-pushed comments are appended below.
    data.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
    const usersToGet = new Set();
    data.forEach((c) => {
      if (!store.state.users[c.userId]) usersToGet.add(c.userId);
    });
    if (usersToGet.size > 0) {
      const res2 = await axios.post("user.php", { type: "getUsersMetaData", users: [...usersToGet] });
      const users = res2.data || [];
      users.forEach((u) => store.commit("setAnUser", u));
    }
    comments.splice(0, comments.length, ...data);
  } catch {
    // non-fatal
  }
}

async function postComment() {
  const text = newComment.value.trim();
  if (!text) return;
  newComment.value = "";
  try {
    /* const { data } = await */ axios.post("/conference.php", form("comment", { text }));
    //comments.push(data);
  } catch (e) {
    error.value = apiError(e, "Could not post comment.");
  }
}

async function deleteComment(comId) {
  try {
    await axios.post("/conference.php", form("deleteComment", { comId }));
    const i = comments.findIndex((c) => c._id === comId);
    if (i !== -1) comments.splice(i, 1);
    closeUserMenu();
  } catch (e) {
    error.value = apiError(e, "Could not delete comment.");
  }
}

// ---------------------------------------------------------------------------
// Exit flow
// ---------------------------------------------------------------------------

async function handleExit() {
  if (isAdmin.value) alertW.value.show("End the live for everyone?")
  else handleExit1()
}
async function handleExit1() {
  // if (isAdmin.value && !confirm("End the live for everyone?")) return;

  ending.value = true;
  try {
    await teardown();

    if (isAdmin.value) {
      await axios.post("/conference.php", form("stopConference"));
      emit("ended");
    } else if (isActor.value) {
      await axios.post("/conference.php", form("leaveConference"));
      emit("left");
    } else {
      await axios.post("/conference.php", form("stopListening"));
      emit("left");
    }
    Display.value = false;
  } catch (e) {
    error.value = apiError(e, "Could not leave the live.");
  } finally {
    ending.value = false;
  }
}

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

function hueFor(identity) {
  let h = 0;
  for (const c of String(identity)) h = (h * 31 + c.charCodeAt(0)) % 360;
  return h;
}

const reversedComments = computed(() => {
  return [...comments].reverse();
});

onBeforeUnmount(() => {
  teardown();
});
</script>

<template>
  <div v-show="Display && conference" @click.self="hide" class="liveContainer">
    <div class="live">
      <template v-if="conference">
        <!-- header -->
        <div class="header">
          <div @click="showInfo = !showInfo" class="host">
            <div
              class="avatar"
              :style="{ background: conference.imgMini ? `url(${bsrImg + conference.imgMini}) no-repeat center / cover` : `hsl(${hueFor(conference.userId)} 65% 42%)` }"
            ></div>
            <div class="host-meta">
              <span class="live-badge"><span class="dot"></span>CONFERENCE</span>
              <span class="title">{{ conference.text }}</span>
            </div>
          </div>

          <div class="header-actions">
            <button class="icon-btn" title="echo cancellation" @click="showEchoInfo = !showEchoInfo">
              <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 18v-6a9 9 0 0 1 18 0v6"></path><path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"></path></svg>
            </button>
            <button
              v-if="isAdmin"
              class="count-btn"
              :title="'banned users'"
              @click="showBanned = !showBanned"
            >
            <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
              <span class="count-number">{{ numParticipants }}</span>
            </button>
            <span v-else class="count-btn static">
              <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
              <span class="count-number">{{ numParticipants }}</span>
            </span>

            <button class="icon-btn" title="share" @click="onShare">
              <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="18" cy="5" r="3"></circle><circle cx="6" cy="12" r="3"></circle><circle cx="18" cy="19" r="3"></circle><line x1="8.6" y1="13.5" x2="15.4" y2="17.5"></line><line x1="15.4" y1="6.5" x2="8.6" y2="10.5"></line></svg>
            </button>

            <button class="icon-btn exit" :disabled="ending" title="leave" @click="handleExit">
              <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
            </button>
          </div>
        </div>

        <!-- info panel: cover (click to open board), description, tags -->
        <div v-if="showInfo" class="overlay" @click.self="showInfo = false">
          <div class="overlay-card">
            <img
              v-if="conference.img"
              :src="bsrImg + conference.img"
              class="info-img"
              title="open the shared board"
              @click="showBoard = true"
            />
            <p v-if="conference.img" class="board-hint">tap the cover to open the shared board</p>

            <div v-if="!editingText" class="info-text-row">
              <p class="info-text">{{ conference.text }}</p>
              <button v-if="isAdmin" class="small-btn" @click="startEditText">edit</button>
            </div>
            <div v-else class="edit-row">
              <textarea v-model="editText" class="edit-textarea" rows="3"></textarea>
              <div class="edit-actions">
                <button class="small-btn" @click="editingText = false">cancel</button>
                <button class="primary" @click="saveText">save</button>
              </div>
            </div>

            <div v-if="conference.tags && conference.tags.length" class="tags-row">
              <span v-for="t in conference.tags" :key="t" class="tag">#{{ t }}</span>
            </div>
            <button class="primary" @click="showInfo = false">close</button>
          </div>
        </div>

        <!-- banned users panel (admin only) -->
        <div v-if="showBanned" class="overlay" @click.self="showBanned = false">
          <div class="overlay-card">
            <h4 class="overlay-title">Banned users</h4>
            <p v-if="!conference.bannedUsers || !conference.bannedUsers.length" class="info-text">
              No banned users.
            </p>
            <ul v-else class="banned-list">
              <li v-for="b in conference.bannedUsers" :key="b" class="banned-row">
                <span class="banned-name">{{ getUser(b).pseudo || b }}</span>
                <button class="small-btn" @click="unbanUser(b)">unban</button>
              </li>
            </ul>
            <button class="primary" @click="showBanned = false">close</button>
          </div>
        </div>

        <!-- comment pop up -->
        <div v-if="selectedComment" class="overlay" @click.self="closeUserMenu">
          <div class="overlay-card user-menu-card">
            <h4 class="overlay-title">{{ getUser(selectedComment.userId).pseudo }}: "{{ selectedComment.text }}"</h4>

            <button class="primary" @click="viewProfile(getUser(selectedComment.userId))">see profile</button>
            <button v-if="isAdmin && !conference.actors.find(act=>act.userId === selectedComment.userId)" class="primary" @click="inviteActor([getUser(selectedComment.userId)])">add as actor</button>

            <button
              v-if="selectedComment.userId === currentUserId || isAdmin"
              class="primary"
              @click="deleteComment(selectedComment._id)"
            >
              delete comment
            </button>
            <button
              v-if="isAdmin && selectedComment.userId !== currentUserId && selectedComment.userId !== conference.userId"
              class="primary"
              @click="toggleBanFromChat(selectedComment.userId)"
            >
              {{ isUserBanned(selectedComment.userId) ? "unban user" : "ban user" }}
            </button>
          </div>
        </div>
        <!-- Echocancellation toggler -->
        <div v-if="showEchoInfo" class="overlay" @click.self="showEchoInfo = false">
          <div class="overlay-card">
            <h4 class="overlay-title">Echo cancellation</h4>
            <p class="info-text">
            <strong>Turn off</strong> if you have a headset for maximum audio quality<br><br>
            <strong>Turn on</strong> to stop hearing your own voice reflected back through your microphone
            </p>
            <label class="echo-toggle">
              <input type="checkbox" :checked="echoCancellation" @change="onEchoCancellationChange($event.target.checked)" />
              echo cancellation is {{ echoCancellation ? "on" : "off" }}
            </label>
            <button class="primary" @click="showEchoInfo = false">close</button>
          </div>
        </div>

        <p v-if="error" class="error-banner">
          <span>{{ error }} </span>
        </p>
        <p v-if="connecting" class="status-banner">connecting…</p>

        <!-- speaker grid: every invited actor, connected or not -->
        <div v-if="!commentsExpanded" class="grid-content">
        <div v-if="!commentsExpanded" class="grid">
          <div
            v-for="actor in actors"
            :key="actor.userId"
            class="tile"
            :class="{ speaking: liveFor(actor.userId).speaking, offline: !isOnline(actor.userId) }"
            @click="
              String(actor.userId) === String(currentUserId)
                ? toggleSelfMute()
                : isAdmin && (openActionsFor = openActionsFor === actor.userId ? null : actor.userId)
            "
          >
            <div class="title-content">
              <div
                class="tile-avatar"
                :style="{ background: getUser(actor.userId).profilImgMini1 ? `url(${bsrImg + getUser(actor.userId).profilImgMini1}) no-repeat center / cover` : `hsl(${hueFor(actor.userId)} 65% 42%)` }"
              >
                <svg v-if="conference.userId === actor.userId" class="crown" viewBox="0 0 24 24" fill="currentColor"><polygon points="12 2 15 9 22 9 16.5 13.5 18.5 21 12 16.5 5.5 21 7.5 13.5 2 9 9 9"></polygon></svg>
                <!-- <svg v-if="actor.state === 'main'" class="crown" viewBox="0 0 24 24" fill="currentColor"><polygon points="12 2 15 9 22 9 16.5 13.5 18.5 21 12 16.5 5.5 21 7.5 13.5 2 9 9 9"></polygon></svg>
               -->
              </div>
              <svg v-if="actor.state === 'mute' || liveFor(actor.userId).micMuted" class="mic-off-badge" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="1" y1="1" x2="23" y2="23"></line><path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"></path><path d="M17 16.95A7 7 0 0 1 5 12v-2"></path><path d="M19 10v2a7 7 0 0 1-.11 1.23"></path><line x1="12" y1="19" x2="12" y2="23"></line><line x1="8" y1="23" x2="16" y2="23"></line></svg>
              <span class="name">
                {{ getUser(actor.userId).pseudo }}<span v-if="String(actor.userId) === String(currentUserId)"> (you)</span>
              </span>
            </div>

            <div v-if="openActionsFor === actor.userId" class="actions-menu" @click.stop>
              <button @click="toggleMuteActor(actor)">{{ actor.state === "mute" ? "unmute" : "mute" }}</button>
              <button @click="removeActor(actor.userId)">remove</button>
              <button class="danger" @click="banActor(actor.userId)">ban</button>
              <button @click="viewProfile(getUser(actor.userId))">see profile</button>
            </div>
          </div>

          <button v-if="isAdmin && openSpeakerSlots > 0" class="title-content ghost" 
            @click="pickerOpen = true">
            <svg class="plus-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
            <span class="name">add speaker</span>
          </button>
        </div>
        </div>

        <!-- comments -->
        <div class="comments" :class="{ expanded: commentsExpanded }">
          <div v-for="c in reversedComments" :key="c._id" class="comment">
            <div
              class="comment-avatar comment-avatar-fallback"
              :style="{ background: getUser(c.userId).profilImgMini1 ? `url(${bsrImg + getUser(c.userId).profilImgMini1}) no-repeat center / cover` : `hsl(${hueFor(c.userId)} 65% 42%)` }"
              ></div>
            <div @click="openUserMenu(c)" class="comment-body">
              <span class="comment-author">{{ getUser(c.userId).pseudo }}</span>
              <span class="comment-text">{{ c.text }}</span>
            </div>
          </div>
        </div>

        <!-- footer -->
        <div class="footer">
          <div class="stats-bar">
            <span class="stat">
              <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
              {{ numParticipants }}
            </span>
            <span class="stat">
              <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8z"></path></svg>
              {{ conference.stats ? (conference.stats.likes || 0) : 0 }}
            </span>
            <span class="stat">
              <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path></svg>
              {{ conference.stats ? (conference.stats.comments || comments?.length) : comments?.length }}
            </span>
            <button
              class="expand-toggle"
              :title="commentsExpanded ? 'reduce comments' : 'expand comments'"
              @click="commentsExpanded = !commentsExpanded"
            >
              <svg v-if="!commentsExpanded" class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="7 13 12 18 17 13"></polyline><polyline points="7 6 12 11 17 6"></polyline></svg>
              <svg v-else class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="17 11 12 6 7 11"></polyline><polyline points="17 18 12 13 7 18"></polyline></svg>
            </button>
          </div>

          <button class="icon-btn like" :class="{ liked: liked }" @click="toggleLike">
            <svg v-if="liked" class="icon" viewBox="0 0 24 24" fill="currentColor"><path d="M12 21s-6.7-4.35-9.3-8.4C1.1 9.7 2 6.4 5 5.2c2-.8 4 0 5 1.8 1-1.8 3-2.6 5-1.8 3 1.2 3.9 4.5 2.3 7.4-2.6 4.05-9.3 8.4-9.3 8.4z"></path></svg>
            <svg v-else class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8z"></path></svg>
          </button>
          <input
            v-model="newComment"
            class="comment-input"
            placeholder="Add comment"
            @keyup.enter="postComment"
          />
          <button v-if="isActor" class="icon-btn mic" :class="{ off: !micEnabled }" @click="toggleSelfMute">
            <svg v-if="micEnabled" class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path><path d="M19 10v2a7 7 0 0 1-14 0v-2"></path><line x1="12" y1="19" x2="12" y2="23"></line><line x1="8" y1="23" x2="16" y2="23"></line></svg>
            <svg v-else class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="1" y1="1" x2="23" y2="23"></line><path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"></path><path d="M17 16.95A7 7 0 0 1 5 12v-2"></path><path d="M19 10v2a7 7 0 0 1-.11 1.23"></path><line x1="12" y1="19" x2="12" y2="23"></line><line x1="8" y1="23" x2="16" y2="23"></line></svg>
          </button>
          <button class="icon-btn send" @click="postComment">
            <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="22" y1="2" x2="11" y2="13"></line><polygon points="22 2 15 22 11 13 2 9 22 2"></polygon></svg>
          </button>
        </div>

        <LiveBoard
          v-if="showBoard"
          :conference-id="conference._id"
          :cover-image="conference.img ? bsrImg + conference.img : null"
          :can-draw="isActor"
          @close="showBoard = false"
          @change-cover="onChangeCover"
        />

        <ParticipantPicker
        :open="pickerOpen"
        :model-value="selectedParticipants"
        :max="10 - conference.actors.length"
        @update:model-value="inviteActor"
        @close="pickerOpen = false"
        />
      </template>
    </div>
    <AlertW type="warning" :showCancel="true" @confirm="handleExit1" ref="alertW" />
  </div>
</template>

<style scoped>
.liveContainer {
  position: fixed;
  top: 0px;
  left: 0px;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  background: rgba(6, 9, 18, 0.6);
}

.live {
  --bg: #0a0e1f;
  --surface: #131a33;
  --surface-2: #1b2444;
  --border: #262f52;
  --text: #eceefb;
  --text-dim: #8890b5;
  --brand-a: #6d5dfc;
  --brand-b: #a855f7;
  --accent: #22d3ee;
  --live: #f43f5e;
  --danger: #f4574a;

  --fs-2xs: 13px;
  --fs-xs: 15px;
  --fs-sm: 16px;
  --fs-md: 18px;
  --fs-lg: 19px;
  --icon-2xs: 14px;
  --icon-sm: 20px;
  --icon-md: 26px;
  --btn-sm: 40px;
  --avatar-sm: 32px;
  --avatar-md: 54px;
  --avatar-tile: 68px;
  --w-title: 200px;
  --w-tile-name: 120px;
  --w-banned-name: 260px;

  display: flex;
  flex-direction: column;
  height: 100%;
  max-width: 640px;
  margin: 0 auto;
  background: var(--bg);
  color: var(--text);
  font-family: ui-sans-serif, system-ui, sans-serif;
  overflow: hidden;
  border: 1px solid var(--border);
  border-radius: 16px;
  max-height: 95%;
  z-index: 2000;
}

@media (max-width: 600px) {
  .live {
    --fs-2xs: 11px;
    --fs-xs: 13px;
    --fs-sm: 14px;
    --fs-md: 16px;
    --fs-lg: 17px;
    --icon-2xs: 12px;
    --icon-sm: 17px;
    --icon-md: 22px;
    --btn-sm: 35px;
    --avatar-sm: 27px;
    --avatar-md: 46px;
    --avatar-tile: 57px;
    --w-title: 165px;
    --w-tile-name: 98px;
    --w-banned-name: 220px;
    max-width: 100%;
    width: 100%;
    height: 100%;
    max-height: 100%;
    border: none;
  }
}

@media (max-width: 400px) {
  .live {
    --fs-2xs: 10px;
    --fs-xs: 12px;
    --fs-sm: 13px;
    --fs-md: 14px;
    --fs-lg: 15px;
    --icon-2xs: 11px;
    --icon-sm: 15px;
    --icon-md: 19px;
    --btn-sm: 32px;
    --avatar-sm: 24px;
    --avatar-md: 40px;
    --avatar-tile: 50px;
    --w-title: 145px;
    --w-tile-name: 85px;
    --w-banned-name: 195px;
  }
}

@media (max-width: 340px) {
  .live {
    --fs-2xs: 10px;
    --fs-xs: 11px;
    --fs-sm: 13px;
    --fs-md: 14px;
    --fs-lg: 14px;
    --icon-2xs: 10px;
    --icon-sm: 14px;
    --icon-md: 18px;
    --btn-sm: 30px;
    --avatar-sm: 22px;
    --avatar-md: 38px;
    --avatar-tile: 46px;
    --w-title: 130px;
    --w-tile-name: 55px;
    --w-banned-name: 180px;
  }
}

.icon {
  width: var(--icon-sm);
  height: var(--icon-sm);
  display: block;
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid var(--border);
}

.host {
  display: flex;
  align-items: center;
  min-width: 0;
  cursor: pointer;
}

.host > * {
  margin-left: 10px;
}

.host > *:first-child {
  margin-left: 0;
}

.avatar {
  width: var(--avatar-md);
  height: var(--avatar-md);
  border-radius: 50%;
  flex-shrink: 0;
}

.host-meta {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.live-badge {
  display: flex;
  align-items: center;
  font-size: var(--fs-2xs);
  font-weight: 700;
  letter-spacing: 0.04em;
  color: var(--live);
}

.live-badge .dot {
  margin-right: 5px;
}

.dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--live);
  box-shadow: 0 0 0 3px rgba(244, 63, 94, 0.2);
  flex-shrink: 0;
}

.title {
  display: inline-block;
  font-size: var(--fs-md);
  color: var(--text-dim);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: var(--w-title);
  vertical-align: top;
}

.header-actions {
  display: flex;
  align-items: center;
}

.header-actions > * {
  margin-left: 8px;
}

.header-actions > *:first-child {
  margin-left: 0;
}

.count-btn {
  display: flex;
  align-items: center;
  border: none;
  background: var(--surface-2);
  color: var(--text-dim);
  border-radius: 999px;
  padding: 5px 10px;
  font-size: var(--fs-xs);
  cursor: pointer;
}

.count-btn.static {
  cursor: default;
}

.count-number {
  margin-left: 5px;
}

.icon-btn {
  border: none;
  background: var(--surface-2);
  color: var(--text);
  border-radius: 50%;
  width: var(--btn-sm);
  height: var(--btn-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
}

.icon-btn.exit {
  background: rgba(244, 87, 74, 0.15);
  color: var(--danger);
}

.error-banner {
  margin: 10px 16px 0;
  font-size: var(--fs-sm);
  color: var(--danger);
  text-align: center;
  
}
.error-banner > span {
  padding:  10px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(10, 14, 38, 0.95);
  border: 1px solid rgba(0, 212, 255, 0.2);
  color: #e0f7ff;
  font-weight: 500;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.status-banner {
  margin: 10px 16px 0;
  font-size: var(--fs-sm);
  color: var(--text-dim);
}

.overlay {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: rgba(6, 8, 20, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 20;
  padding: 24px;
}

.overlay-card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 14px;
  padding: 16px;
  max-width: 320px;
  width: 100%;
  max-height: 80%;
  overflow-y: auto;
}

.overlay-card > * {
  margin-top: 12px;
}

.overlay-card > *:first-child {
  margin-top: 0;
}

.overlay-title {
  font-size: var(--fs-lg);
  color: var(--text);
  margin: 0;
}

.info-img {
  width: 100%;
  border-radius: 10px;
  object-fit: cover;
  max-height: 200px;
  display: block;
  cursor: pointer;
}

.board-hint {
  font-size: var(--fs-2xs);
  color: var(--text-dim);
  text-align: center;
  margin: 5px 0 0;
}

.info-text-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
}

.info-text-row > * {
  margin-left: 8px;
}

.info-text-row > *:first-child {
  margin-left: 0;
}

.info-text {
  font-size: var(--fs-md);
  color: var(--text);
  line-height: 1.4;
  flex: 1;
  min-width: 0;
}

.edit-row {
  display: flex;
  flex-direction: column;
}

.edit-textarea {
  width: 100%;
  background: var(--bg);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 8px;
  color: var(--text);
  font-size: var(--fs-sm);
  font-family: inherit;
  resize: vertical;
}

.edit-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 8px;
}

.edit-actions > * {
  margin-left: 8px;
}

.tags-row {
  display: flex;
  flex-wrap: wrap;
}

.tag {
  background: var(--surface-2);
  color: var(--accent);
  font-size: var(--fs-xs);
  padding: 3px 8px;
  border-radius: 999px;
  margin: 0 5px 5px 0;
  display: inline-block;
}

.banned-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.banned-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 0;
  border-bottom: 1px solid var(--border);
}

.banned-name {
  font-size: var(--fs-sm);
  color: var(--text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  display: inline-block;
  max-width: var(--w-banned-name);
}

.small-btn {
  background: var(--surface-2);
  color: var(--text);
  border: 1px solid var(--border);
  border-radius: 6px;
  padding: 4px 9px;
  font-size: var(--fs-xs);
  cursor: pointer;
  flex-shrink: 0;
  margin-left: 8px;
}

.grid-content {
  min-height:50%;
  overflow: auto;
}
.grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  padding: 16px;
}

.tile {
  position: relative;
  margin: 0 10px 10px 0;
}

.title-content {
  min-width: 0;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 12px 6px;
  cursor: pointer;
  transition: opacity 0.15s ease;

  display: flex;
  flex-direction: column;
  align-items: center;
}

.title-content:nth-child(3n) {
  margin-right: 0;
}

.speaking .title-content {
  border-color: var(--accent);
  box-shadow: 0 0 0 2px rgba(34, 211, 238, 0.2);
}

.offline .title-content {
  opacity: 0.45;
  border-style: dashed;
}

.title-content.ghost {
  justify-content: center;
  color: var(--text-dim);
  background: transparent;
  border-style: dashed;
  margin: 0 10px 10px 0;
}

.plus-icon {
  width: var(--icon-md);
  height: var(--icon-md);
  margin-bottom: 5px;
}

.tile-avatar {
  position: relative;
  width: var(--avatar-tile);
  height: var(--avatar-tile);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 5px;
  flex-shrink: 0;
}

.crown {
  position: absolute;
  top: -6px;
  right: -4px;
  width: var(--icon-2xs);
  height: var(--icon-2xs);
  color: #facc15;
}

.mic-off-badge {
  position: absolute;
  top: 6px;
  right: 6px;
  width: var(--icon-2xs);
  height: var(--icon-2xs);
  color: var(--danger);
}

.name {
  display: block;
  width: 100%;
  min-width: 0;
  font-size: var(--fs-xs);
  color: var(--text-dim);
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: var(--w-tile-name);
}

.actions-menu {
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  z-index: 5;
  display: flex;
  flex-direction: column;
  background: var(--surface-2);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 6px;
  margin-top: 5px;
  min-width: 120px;
}

.actions-menu > * {
  margin-top: 4px;
}

.actions-menu > *:first-child {
  margin-top: 0;
}

.actions-menu button {
  background: transparent;
  border: none;
  color: var(--text);
  font-size: var(--fs-sm);
  padding: 6px 6px;
  text-align: left;
  border-radius: 6px;
  cursor: pointer;
}

.actions-menu button:hover {
  background: rgba(255, 255, 255, 0.06);
}

.actions-menu button.danger {
  color: var(--danger);
}

.invite-row {
  display: flex;
  padding: 0 16px 16px;
}

.invite-row > * {
  margin-left: 8px;
}

.invite-row > *:first-child {
  margin-left: 0;
}

.invite-row input {
  flex: 1;
  min-width: 0;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 8px 10px;
  color: var(--text);
  font-size: var(--fs-sm);
}

button.primary {
  background: linear-gradient(135deg, var(--brand-a), var(--brand-b));
  color: white;
  border: none;
  border-radius: 8px;
  padding: 8px 14px;
  font-size: var(--fs-sm);
  font-weight: 600;
  cursor: pointer;
}

.comments {
  flex: 1;
  overflow-y: auto;
  padding: 0 16px;
  padding-bottom: 60px;
  margin-top: 10px;
}

.comments > * {
  margin-top: 6px;
}

.comments > *:first-child {
  margin-top: 0;
}

.comment {
  display: flex;
  font-size: var(--fs-sm);
  line-height: 1.4;
  min-width: 0;
}

.comment-avatar {
  width: var(--avatar-sm);
  height: var(--avatar-sm);
  border-radius: 50%;
  flex-shrink: 0;
  margin-right: 6px;
  object-fit: cover;
  cursor:pointer;
}

.comment-body {
  min-width: 0;
  cursor:pointer;
}

.comment-author {
  color: var(--accent);
  font-weight: 600;
  margin-right: 6px;
}

.comment-text {
  color: var(--text);
  word-break: break-word;
}

.footer {
  position: sticky;
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-top: 1px solid var(--border);
}
.stats-bar {
  position: absolute;
  bottom: 100%;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  padding: 6px 16px;
  background: rgba(19, 26, 51, 0.9);
  border-top: 1px solid var(--border);
}

.stats-bar .stat {
  display: flex;
  align-items: center;
  font-size: var(--fs-xs);
  color: var(--text-dim);
  margin-left: 13px;
}

.stats-bar .stat:first-child {
  margin-left: 0;
}

.stats-bar .stat .icon {
  margin-right: 4px;
}

.expand-toggle {
  margin-left: auto;
  border: none;
  background: var(--surface-2);
  color: var(--text-dim);
  border-radius: 50%;
  width: var(--btn-sm);
  height: var(--btn-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
}

.comments.expanded {
  flex: 1 1 auto;
}

.footer > * {
  margin-left: 8px;
}

.footer > *:first-child {
  margin-left: 0;
}

.comment-input {
  flex: 1;
  min-width: 0;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 999px;
  padding: 9px 14px;
  color: var(--text);
  font-size: var(--fs-sm);
}

.icon-btn.mic {
  background: rgba(34, 211, 238, 0.15);
  color: var(--accent);
}

.icon-btn.mic.off {
  background: rgba(244, 87, 74, 0.15);
  color: var(--danger);
}

.icon-btn.send {
  background: linear-gradient(135deg, var(--brand-a), var(--brand-b));
  color: white;
}

.icon-btn.like {
  background: var(--surface-2);
  color: var(--text-dim);
}

.icon-btn.like.liked {
  background: rgba(244, 63, 94, 0.15);
  color: var(--live);
}


/* comment pop up  */
.user-menu-card {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  text-align: center;
}

.user-menu-card > * {
  margin-top: 8px;
}

.user-menu-card > *:first-child {
  margin-top: 0;
}

.danger-btn {
  color: var(--danger);
  border-color: var(--danger);
}

/* echo cancelllation menu */
.echo-toggle {
  display: flex;
  align-items: center;
  font-size: var(--fs-sm);
  color: var(--text);
  cursor: pointer;
}
.echo-toggle input {
  margin-right: 0.4rem;
}
</style>